import type { Zero } from "@rocicorp/zero";
import { ZeroProvider } from "@rocicorp/zero/react";
import * as Sentry from "@sentry/react";
import { type ReactNode, useEffect, useMemo } from "react";
import type { Context } from "../../shared/authorization";
import { schema } from "../../shared/generatedDrizzleSchema";
import { sharedMutators } from "../../shared/mutators";
import { CHAT_CREATING_MUTATIONS, gatedMutate } from "../hooks/serverGate";
import { emitErrorToast } from "../lib/errorToast";
import { getTraceparent } from "../lib/otel";
import { apiUrl, zeroSyncUrl } from "../lib/tenantOrigin";
import {
  initTracing,
  sessionId,
  trackConnectionState,
  trackMutation,
} from "../lib/tracing";
import { zeroLogger } from "../lib/zeroLogger";
import { classifyZeroResult, type ZeroResult } from "../lib/zeroResultError";

function initZero(z: Zero) {
  patchMutate(z);
  trackConnectionState(z);
  reportConnectionErrors(z);
}

// Following https://zero.rocicorp.dev/docs/connection#error
function reportConnectionErrors(z: Zero) {
  let inErrorState = false;
  z.connection.state.subscribe((state) => {
    if (state.name !== "error") {
      inErrorState = false;
      return;
    }
    if (inErrorState) return;
    inErrorState = true;
    Sentry.withScope((scope) => {
      scope.setTag("zero.connection.state", state.name);
      scope.setExtra("zero.connection.reason", state.reason);
      Sentry.captureException(
        new Error(`Zero connection error: ${state.reason}`)
      );
    });
  });
}

function patchMutate(z: Zero) {
  const origMutate = z.mutate;
  const handleError = (r: ZeroResult, scope: string) => {
    const action = classifyZeroResult(r);
    if (action.kind === "ignore") return;
    if (action.message === undefined && scope !== "server") return;
    emitErrorToast({
      icon: "circle-off",
      message: action.message ?? "Something went wrong. Please try again.",
      buttonLabel: "Dismiss",
    });
  };
  (z as unknown as Record<string, unknown>).mutate = Object.assign(
    // oxlint-disable-next-line typescript/no-explicit-any -- patching Zero's callable mutate at init
    (mr: any) => {
      const start = performance.now();
      const name: string = mr?.mutator?.mutatorName ?? "unknown";
      const result = origMutate(mr);
      if (CHAT_CREATING_MUTATIONS.has(name)) gatedMutate(result);
      result.client.then((r: ZeroResult) => {
        handleError(r, "client");
        trackMutation(name, start, "client", r);
      });
      result.server.then((r: ZeroResult) => {
        handleError(r, "server");
        trackMutation(name, start, "server", r);
      });
      return result;
    },
    origMutate
  );
}

const sessionHeaders = { "x-session-id": sessionId };

export function ZeroSetup({
  user,
  children,
}: {
  user: { id: string; tenantId: string };
  children: ReactNode;
}) {
  const context: Context = useMemo(
    () => ({ userId: user.id, tenantId: user.tenantId }),
    [user.id, user.tenantId]
  );

  useEffect(() => {
    initTracing(apiUrl(), user.tenantId);
  }, [user.tenantId]);

  return (
    <ZeroProvider
      userID={user.id}
      cacheURL={zeroSyncUrl()}
      kvStore="mem"
      schema={schema}
      context={context}
      mutators={sharedMutators}
      init={initZero}
      queryHeaders={sessionHeaders}
      mutateHeaders={sessionHeaders}
      getTraceparent={getTraceparent}
      {...zeroLogger}
    >
      {children}
    </ZeroProvider>
  );
}
